import { useRef, useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  Tooltip,
  IconButton,
  Button,
} from "@mui/material";
import CropFreeIcon from "@mui/icons-material/CropFree";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";

// One distinct color per field (cycles if more than 10 fields)
const FIELD_COLORS = [
  "#1565C0", "#2e7d32", "#c62828", "#6a1b9a", "#e65100",
  "#00695c", "#0277bd", "#558b2f", "#ad1457", "#4527a0",
];

const getColor = (idx) => FIELD_COLORS[idx % FIELD_COLORS.length];

export const RegionesEditor = ({ metadatos, regiones, onChange, imagenReferencia, onImagenChange, readonly = false }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  // activeField: index into metadatos that user is currently drawing, or null
  const [activeField, setActiveField] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [startPos, setStartPos] = useState(null);
  const [liveRect, setLiveRect] = useState(null);

  // Local map: id_metadatos → { x, y, width, height } in canvas coords (pixels)
  const [drawnRects, setDrawnRects] = useState({});
  const [canvasReady, setCanvasReady] = useState(false);

  // Draw all regions on canvas (transparent — img element underneath handles the visual)
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !img.complete || !img.naturalWidth) return;

    // Canvas internal pixels = natural image size for accurate coordinate mapping
    if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      setCanvasReady(true);
    }

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // NOTE: do NOT drawImage here — the <img> below provides the visual layer

    // Draw saved regions
    metadatos.forEach((meta, idx) => {
      const rect = drawnRects[meta.id];
      if (!rect) return;
      const color = getColor(idx);

      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);

      // Semi-transparent fill
      ctx.fillStyle = color + "22";
      ctx.fillRect(rect.x, rect.y, rect.width, rect.height);

      // Label background
      const label = meta.label_form || `Campo ${idx + 1}`;
      ctx.font = "bold 11px sans-serif";
      const textW = ctx.measureText(label).width + 8;
      ctx.fillStyle = color;
      ctx.fillRect(rect.x, rect.y - 18, textW, 18);
      ctx.fillStyle = "#fff";
      ctx.fillText(label, rect.x + 4, rect.y - 5);
    });

    // Draw live rectangle being drawn
    if (liveRect && activeField !== null) {
      const color = getColor(activeField);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.strokeRect(liveRect.x, liveRect.y, liveRect.width, liveRect.height);
      ctx.setLineDash([]);
    }
  }, [drawnRects, liveRect, activeField, metadatos]);

  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  // Restore drawn rects from regiones prop when canvas is first ready
  useEffect(() => {
    if (!canvasReady) return;
    const canvas = canvasRef.current;
    if (!canvas?.width || !regiones?.length) return;
    const restored = {};
    regiones.forEach((r) => {
      restored[r.id_metadatos] = {
        x: r.x * canvas.width,
        y: r.y * canvas.height,
        width: r.width * canvas.width,
        height: r.height * canvas.height,
      };
    });
    setDrawnRects(restored);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasReady]);

  const getCanvasPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const handleMouseDown = (e) => {
    if (activeField === null) return;
    const pos = getCanvasPos(e);
    setDrawing(true);
    setStartPos(pos);
    setLiveRect(null);
  };

  const handleMouseMove = (e) => {
    if (!drawing || !startPos) return;
    const pos = getCanvasPos(e);
    setLiveRect({
      x: Math.min(startPos.x, pos.x),
      y: Math.min(startPos.y, pos.y),
      width: Math.abs(pos.x - startPos.x),
      height: Math.abs(pos.y - startPos.y),
    });
  };

  const handleMouseUp = (e) => {
    if (!drawing || !startPos || activeField === null) return;
    const pos = getCanvasPos(e);
    const rect = {
      x: Math.min(startPos.x, pos.x),
      y: Math.min(startPos.y, pos.y),
      width: Math.abs(pos.x - startPos.x),
      height: Math.abs(pos.y - startPos.y),
    };

    // Ignore tiny accidental clicks
    if (rect.width < 5 || rect.height < 5) {
      setDrawing(false);
      setLiveRect(null);
      return;
    }

    const canvas = canvasRef.current;
    const meta = metadatos[activeField];
    const newRects = {
      ...drawnRects,
      [meta.id]: rect,
    };
    setDrawnRects(newRects);
    setLiveRect(null);
    setDrawing(false);

    // Convert to ratios and call onChange
    const nuevasRegiones = metadatos
      .filter((m) => newRects[m.id])
      .map((m) => ({
        id_metadatos: m.id,
        label: m.label_form || `Campo ${m.id}`,
        x: newRects[m.id].x / canvas.width,
        y: newRects[m.id].y / canvas.height,
        width: newRects[m.id].width / canvas.width,
        height: newRects[m.id].height / canvas.height,
      }));
    onChange(nuevasRegiones);

    // Auto-advance to next field
    const nextField = metadatos.findIndex((_, i) => i > activeField && !newRects[metadatos[i]?.id]);
    setActiveField(nextField >= 0 ? nextField : null);
  };

  const handleDeleteRegion = (metaId) => {
    const newRects = { ...drawnRects };
    delete newRects[metaId];
    setDrawnRects(newRects);

    const canvas = canvasRef.current;
    const nuevasRegiones = metadatos
      .filter((m) => newRects[m.id])
      .map((m) => ({
        id_metadatos: m.id,
        label: m.label_form || `Campo ${m.id}`,
        x: newRects[m.id].x / canvas.width,
        y: newRects[m.id].y / canvas.height,
        width: newRects[m.id].width / canvas.width,
        height: newRects[m.id].height / canvas.height,
      }));
    onChange(nuevasRegiones);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      onImagenChange(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const drawnCount = Object.keys(drawnRects).length;

  if (!imagenReferencia) {
    if (readonly) return null;
    return (
      <Box sx={{ p: 3 }}>
        <Paper
          variant="outlined"
          sx={{
            borderStyle: "dashed",
            borderRadius: 2,
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            backgroundColor: "#fafafa",
          }}
        >
          <UploadFileIcon sx={{ fontSize: 40, color: "text.secondary" }} />
          <Typography variant="body2" color="text.secondary">
            Cargá una imagen de referencia para definir las regiones
          </Typography>
          <Button
            variant="outlined"
            component="label"
            size="small"
            startIcon={<UploadFileIcon />}
          >
            Seleccionar imagen
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageUpload}
            />
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, display: "flex", gap: 2, height: "100%", flexDirection: { xs: "column", md: "row" } }}>
      {/* Left: field list */}
      <Box sx={{ width: { xs: "100%", md: 220 }, flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0A395F", mb: 1 }}>
          {readonly ? "Regiones definidas" : "Campos a definir"}
        </Typography>
        {!readonly && (
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ mb: 2, py: 0.5, fontSize: "0.75rem", borderRadius: 1.5 }}
          >
            Selecciona un campo y dibuja su region en la imagen de referencia.
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {metadatos.map((meta, idx) => {
            const isDrawn = !!drawnRects[meta.id];
            const isActive = !readonly && activeField === idx;
            const color = getColor(idx);

            return (
              <Paper
                key={meta.id}
                variant="outlined"
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  borderColor: isActive ? color : isDrawn ? color + "88" : "divider",
                  backgroundColor: isActive ? color + "11" : isDrawn ? color + "08" : "#fff",
                  cursor: readonly ? "default" : "pointer",
                  transition: "all 0.15s",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
                onClick={() => !readonly && setActiveField(isActive ? null : idx)}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" sx={{ fontWeight: isActive ? 700 : 500, flex: 1, fontSize: "0.72rem" }}>
                  {meta.label_form || `Campo ${idx + 1}`}
                </Typography>
                {isDrawn && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "#2e7d32" }} />
                    {!readonly && (
                      <Tooltip title="Borrar región">
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          onClick={(e) => { e.stopPropagation(); handleDeleteRegion(meta.id); }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 14, color: "#9e9e9e" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
                {!readonly && !isDrawn && isActive && (
                  <CropFreeIcon sx={{ fontSize: 14, color }} />
                )}
              </Paper>
            );
          })}
        </Box>

        {drawnCount > 0 && (
          <Box sx={{ mt: 2 }}>
            <Chip
              icon={<CheckCircleOutlineIcon />}
              label={`${drawnCount} de ${metadatos.length} definidas`}
              color="success"
              size="small"
              variant="outlined"
            />
          </Box>
        )}

        {!readonly && drawnCount === 0 && (
          <Alert severity="warning" sx={{ mt: 2, py: 0.5, fontSize: "0.75rem", borderRadius: 1.5 }}>
            Sin regiones: el OCR sera omitido y los metadatos se completaran manualmente.
          </Alert>
        )}

        {!readonly && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="text"
              size="small"
              component="label"
              startIcon={<UploadFileIcon />}
              sx={{ fontSize: "0.72rem" }}
            >
              Cambiar imagen
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageUpload}
              />
            </Button>
          </Box>
        )}
      </Box>

      {/* Right: canvas */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0A395F" }}>
            Imagen de referencia
          </Typography>
          {!readonly && activeField !== null && (
            <Chip
              label={`Dibujando: ${metadatos[activeField]?.label_form || `Campo ${activeField + 1}`}`}
              size="small"
              sx={{ backgroundColor: getColor(activeField), color: "#fff", fontWeight: 600 }}
            />
          )}
        </Box>

        {/* Scrollable wrapper so tall images don't overflow the dialog */}
        <Box sx={{ overflowY: "auto", maxHeight: "62vh", borderRadius: 2, border: "1px solid", borderColor: !readonly && activeField !== null ? getColor(activeField) : "divider", transition: "border-color 0.2s" }}>
          {/* Inner: img drives natural aspect ratio, canvas overlays as transparent drawing layer */}
          <Box sx={{ position: "relative", lineHeight: 0, backgroundColor: "#f5f5f5" }}>
            <img
              ref={imgRef}
              src={imagenReferencia}
              alt="referencia"
              style={{ display: "block", width: "100%", height: "auto" }}
              onLoad={redrawCanvas}
            />
            <canvas
              ref={canvasRef}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                cursor: !readonly && activeField !== null ? "crosshair" : "default",
              }}
              onMouseDown={readonly ? undefined : handleMouseDown}
              onMouseMove={readonly ? undefined : handleMouseMove}
              onMouseUp={readonly ? undefined : handleMouseUp}
              onMouseLeave={readonly ? undefined : () => { if (drawing) { setDrawing(false); setLiveRect(null); } }}
            />
          </Box>
        </Box>

        {!readonly && activeField === null && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, textAlign: "center" }}>
            Selecciona un campo de la lista para comenzar a dibujar su region.
          </Typography>
        )}
      </Box>
    </Box>
  );
};
